import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { toast } from 'react-toastify';
import * as reportesRecepcionService from '../../services/reportes/reportesRecepcionService';
import { listarTiposTurno } from '../../services/tipoTurnoService';
import DatosMaestrosService from '../../services/datosMaestrosService';
import { usePDFReportes } from '../../components/ReportesPDF';
import { useExcelReportes } from '../../components/ReportesExcel';
import { formatearTipoTramite } from '../../constants/tiposTramiteTurno';
import { COLOR, FUENTE } from './reportes/tokens';
import { usePeriodoReportes } from './reportes/periodoReportes';
import BarraPeriodo from './reportes/BarraPeriodo';
import NavegacionReportesRecepcion from './reportes/NavegacionReportesRecepcion';
import ResultadoAtencionChart from './reportes/ResultadoAtencionChart';
import EmbudoTurnos from './reportes/EmbudoTurnos';
import {
    BotonSecundario, EncabezadoReportes, SelectFiltro, TablaReporte, TarjetaReporte,
    TIPO_EXCLUIDO_DE_ESTADISTICAS, TIPOS_TRAMITE_FALLBACK,
} from './reportes/ComponentesReportes';
import KpiRow from './reportes/KpiRow';
import { avisoPrimerTurno, calcularPeriodoAnterior, periodoAnteriorSinCobertura } from './reportes/fechasReportes';
import { filasConversion, filasMotivos, tablaPorDia, tarjetasKpi } from './reportes/vistaSimple';

const DATOS_VACIOS = { resumen: null, conversionPorTipo: [], resultadoAtencion: [], motivosNoAvance: [], porDiaResultado: [] };

async function cargarDatosVista(filtros) {
    const [resumen, conversionPorTipo, resultadoAtencion, motivosNoAvance, porDiaResultado] = await Promise.all([
        reportesRecepcionService.getResumen(filtros),
        reportesRecepcionService.getConversionPorTipo(filtros),
        reportesRecepcionService.getResultadoAtencion(filtros),
        reportesRecepcionService.getMotivosNoAvance(filtros),
        reportesRecepcionService.getPorDiaResultado(filtros),
    ]);
    const lista = (v) => (Array.isArray(v) ? v : []);
    return {
        resumen: resumen || null,
        conversionPorTipo: lista(conversionPorTipo),
        resultadoAtencion: lista(resultadoAtencion),
        motivosNoAvance: lista(motivosNoAvance),
        porDiaResultado: lista(porDiaResultado),
    };
}

/**
 * Reportes Recepción — Resumen (la vista que abre por defecto) y Estadísticas del bot.
 *
 * POR QUÉ EXISTE
 * Las estadísticas completas (ahora "Estadísticas Pro") se le complicaban al estudio. El Resumen
 * muestra solo lo que llevaban en su planilla de Excel: cuántos se presentaron y la conversión por
 * tipo, el resultado de la atención, los motivos de no avance y los turnos atendidos por día. Sin
 * alertas.
 *
 * Con `soloBot` es la misma vista filtrada por la referencia marcada como bot en Datos Maestros
 * (referencias.es_bot, V226): ReportesRecepcionBot.js.
 */
function ReportesRecepcion({ soloBot = false }) {
    const periodo = usePeriodoReportes('semana');
    const { desde, hasta, vacio } = periodo;

    const [filtroTipoTramite, setFiltroTipoTramite] = useState('');
    // Tipo de visita (V229): '' todos, 'NUEVOS' los que no tenían rastro previo en el sistema,
    // 'YA_CLIENTES' los que ya eran clientes del estudio (carpeta marcada, o trámite/visita anterior).
    const [filtroVisita, setFiltroVisita] = useState('');
    const [tiposTramite, setTiposTramite] = useState(TIPOS_TRAMITE_FALLBACK);
    // undefined = cargando, null = no hay ninguna referencia marcada como bot
    const [referenciaBot, setReferenciaBot] = useState(undefined);
    const [datos, setDatos] = useState(DATOS_VACIOS);
    const [anteriores, setAnteriores] = useState(null); // { resumen, conversionPorTipo } del período anterior
    // true si el período anterior empieza antes del primer turno del sistema: no se compara (ese tramo
    // está vacío, no en cero) y la tarjeta lo aclara.
    const [sinComparacion, setSinComparacion] = useState(false);
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        listarTiposTurno(true)
            .then((lista) => {
                // Solo los tipos que entran en las estadísticas (V237): filtrar por uno excluido daría vacío.
                const codigos = (lista || [])
                    .filter((t) => t?.cuentaEnEstadisticas !== false)
                    .map((t) => t?.codigo).filter((c) => c && c !== TIPO_EXCLUIDO_DE_ESTADISTICAS);
                if (codigos.length) setTiposTramite(codigos);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!soloBot) return;
        DatosMaestrosService.getReferencias()
            .then((res) => {
                const lista = Array.isArray(res?.data) ? res.data : [];
                setReferenciaBot(lista.find((r) => r?.esBot) || null);
            })
            .catch(() => setReferenciaBot(null));
    }, [soloBot]);

    const idBot = referenciaBot?.id ?? null;
    const esperandoBot = soloBot && referenciaBot === undefined;
    const sinBot = soloBot && referenciaBot === null;

    useEffect(() => {
        if (esperandoBot) return undefined;
        let vigente = true;
        if (vacio || sinBot) {
            setDatos(DATOS_VACIOS);
            setAnteriores(null);
            setSinComparacion(false);
            setCargando(false);
            return () => { vigente = false; };
        }
        setCargando(true);
        const filtrosComunes = {
            tipoTramite: filtroTipoTramite || null,
            yaEraCliente: filtroVisita === 'NUEVOS' ? false : filtroVisita === 'YA_CLIENTES' ? true : null,
            ...(soloBot ? { referencia: idBot } : {}),
        };
        const filtros = { ...filtrosComunes, fechaDesde: desde || null, fechaHasta: hasta || null };
        // Comparación "vs. período anterior" de las tarjetas, igual que en Pro: best-effort, si falla
        // las tarjetas se muestran igual, sin la variación.
        const periodoAnterior = calcularPeriodoAnterior(desde, hasta);
        const filtrosAnteriores = { ...filtrosComunes, fechaDesde: periodoAnterior.desde, fechaHasta: periodoAnterior.hasta };
        const cargarAnteriores = periodoAnterior.desde
            ? Promise.all([
                reportesRecepcionService.getResumen(filtrosAnteriores),
                reportesRecepcionService.getConversionPorTipo(filtrosAnteriores),
            ])
                .then(([resumen, conversionPorTipo]) => ({ resumen, conversionPorTipo: Array.isArray(conversionPorTipo) ? conversionPorTipo : [] }))
                .catch(() => null)
            : Promise.resolve(null);
        Promise.all([cargarDatosVista(filtros), cargarAnteriores])
            .then(([resultado, datosAnteriores]) => {
                if (!vigente) return;
                setDatos(resultado);
                const sinCobertura = periodoAnteriorSinCobertura(periodoAnterior.desde, resultado.resumen?.primerTurno);
                setSinComparacion(sinCobertura);
                setAnteriores(sinCobertura ? null : datosAnteriores);
            })
            .catch((err) => {
                console.error('Error cargando los reportes de recepción:', err);
                if (vigente) toast.error('No se pudieron cargar los reportes. Probá de nuevo o avisá a soporte si persiste.');
            })
            .finally(() => { if (vigente) setCargando(false); });
        return () => { vigente = false; };
    }, [desde, hasta, vacio, filtroTipoTramite, filtroVisita, soloBot, idBot, esperandoBot, sinBot]);

    const tarjetas = useMemo(() => tarjetasKpi(datos, anteriores, { sinComparacion }), [datos, anteriores, sinComparacion]);
    const conTramiteTotal = useMemo(
        () => datos.conversionPorTipo.reduce((acc, c) => acc + (c.conTramite || 0), 0),
        [datos.conversionPorTipo]
    );
    const avisoPeriodo = avisoPrimerTurno(desde, datos.resumen?.primerTurno);
    const conversion = useMemo(() => filasConversion(datos, formatearTipoTramite), [datos]);
    const motivos = useMemo(() => filasMotivos(datos), [datos]);
    const dias = useMemo(() => tablaPorDia(datos.porDiaResultado, desde, hasta), [datos, desde, hasta]);

    const nombreBot = referenciaBot?.nombre || 'BOT';
    const deQuienes = soloBot ? 'que llegaron por el bot' : 'que se presentaron';

    const { generarYDescargar } = usePDFReportes();
    const { generarYDescargar: generarExcelYDescargar } = useExcelReportes();
    const configExportacion = () => ({
        tipo: 'RECEPCION',
        vista: soloBot ? 'bot' : 'simple',
        datos, nombreBot, desde, hasta,
        formatearTipo: formatearTipoTramite,
        filtros: {
            periodo: periodo.etiqueta,
            tipoTramite: filtroTipoTramite ? formatearTipoTramite(filtroTipoTramite) : 'Todos',
            tipoVisita: filtroVisita === 'NUEVOS' ? 'Clientes nuevos'
                : filtroVisita === 'YA_CLIENTES' ? 'Ya eran clientes' : 'Todas',
        },
    });

    const cantidadTurnos = datos.resumen ? `${(datos.resumen.totalTurnos ?? 0).toLocaleString('es-AR')} turnos en el período` : null;
    const subtitulo = soloBot
        ? [`Solo turnos con la referencia ${nombreBot} (cómo llegó la persona, no cómo se cargó el turno)`, cantidadTurnos].filter(Boolean).join(' · ')
        : (cantidadTurnos || (cargando ? 'Cargando…' : 'Sin turnos en el período'));

    return (
        <Box className="mob-fondo-completo area-recepcion" sx={{ background: '#F4F5F7', minHeight: '100vh', margin: { xs: '-0.75rem', md: '-1.5rem' }, padding: { xs: '0.75rem', md: '1.5rem' } }}>
            <EncabezadoReportes
                migas={soloBot
                    ? [{ label: 'Reportes', to: '/reportes' }, { label: 'Recepción', to: '/reportes/recepcion' }, { label: 'Estadísticas del bot' }]
                    : [{ label: 'Reportes', to: '/reportes' }, { label: 'Recepción' }]}
                titulo={soloBot ? 'Estadísticas del bot' : 'Reportes Recepción'}
                subtitulo={subtitulo}
                acciones={(
                    <>
                        <BotonSecundario
                            onClick={() => generarExcelYDescargar(configExportacion(), soloBot ? 'estadisticas_bot_recepcion' : 'reporte_recepcion')}
                            disabled={cargando || sinBot}
                        >
                            Exportar Excel
                        </BotonSecundario>
                        <BotonSecundario primario onClick={() => generarYDescargar(configExportacion())} disabled={cargando || sinBot}>
                            Exportar PDF
                        </BotonSecundario>
                    </>
                )}
            />

            <NavegacionReportesRecepcion activa={soloBot ? 'bot' : 'resumen'} />

            <BarraPeriodo periodo={periodo} />

            {avisoPeriodo && (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 12, color: COLOR.grisTexto, marginBottom: '12px' }}>
                    {avisoPeriodo}
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
                <SelectFiltro
                    label="Tipo de trámite" valor={filtroTipoTramite} onChange={setFiltroTipoTramite}
                    opciones={tiposTramite.map((t) => ({ valor: t, etiqueta: formatearTipoTramite(t) }))}
                />
                {/* Las estadísticas que le sirven al estudio son las de la gente nueva: este filtro
                    parte el período en dos según si la persona ya era cliente. */}
                <SelectFiltro
                    label="Tipo de visita" valor={filtroVisita} onChange={setFiltroVisita}
                    opciones={[
                        { valor: 'NUEVOS', etiqueta: 'Clientes nuevos' },
                        { valor: 'YA_CLIENTES', etiqueta: 'Ya eran clientes' },
                    ]}
                />
            </Box>

            {sinBot ? (
                <TarjetaReporte titulo="No hay ninguna referencia marcada como bot">
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>
                        Estas estadísticas usan la referencia marcada como bot en Datos Maestros. Pedile a soporte que la marque para ver los turnos del bot.
                    </Typography>
                </TarjetaReporte>
            ) : cargando ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', '& > *': { minWidth: 0, maxWidth: '100%' } }}>
                    <Skeleton variant="rounded" height={90} />
                    <Skeleton variant="rounded" height={260} />
                    <Skeleton variant="rounded" height={260} />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', '& > *': { minWidth: 0, maxWidth: '100%' } }}>
                    {/* 1. Tarjetas principales: las de Estadísticas Pro, sin metas ni espera media */}
                    {tarjetas.length > 0 && <KpiRow items={tarjetas} />}

                    {/* Embudo del turno: mismos datos y mismo componente que en Estadísticas Pro */}
                    {datos.resumen && (
                        <EmbudoTurnos
                            agendado={datos.resumen.totalTurnos ?? 0}
                            presentaron={datos.resumen.turnosAtendidos ?? 0}
                            conTramite={conTramiteTotal}
                            noAsistio={datos.resumen.turnosNoAsistio ?? 0}
                            cancelados={datos.resumen.turnosCancelados ?? 0}
                        />
                    )}

                    {/* 2. Conversión por tipo, sobre las atenciones finalizadas */}
                    <TarjetaReporte
                        titulo="Conversión por tipo de trámite"
                        subtitulo="Por tipo de turno. Conversión = trámites abiertos en la atención sobre las atenciones finalizadas. No cuentan los tipos de turno que no son atención al público (cobros, turnos médicos...) ni quien vino por un trámite que ya tenía en curso."
                    >
                        <TablaReporte
                            columnas={[
                                { titulo: 'Tipo de trámite' },
                                { titulo: 'Atenciones' },
                                { titulo: 'Trámite abierto' },
                                { titulo: 'Conversión', porcentaje: true },
                            ]}
                            filas={conversion.map((f) => ({
                                clave: f.tipo,
                                celdas: [f.etiqueta, f.atenciones, f.conTramite, f.conversion],
                            }))}
                        />
                    </TarjetaReporte>

                    {/* 3. Resultado de la atención y motivos de no avance */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: '16px', alignItems: 'start' }}>
                        <ResultadoAtencionChart
                            data={datos.resultadoAtencion}
                            titulo="Resultado de la atención"
                            subtitulo={`En qué terminó la atención de las personas ${deQuienes}. Inicia ahora + Carpeta a futuro son las atenciones en las que se abrió un trámite.`}
                        />
                        <TarjetaReporte titulo="Motivos de no avance" subtitulo="Por qué la atención no terminó en un trámite.">
                            <TablaReporte
                                columnas={[{ titulo: 'Motivo' }, { titulo: 'Cantidad' }, { titulo: '% del total', porcentaje: true }]}
                                filas={motivos.map((f) => ({ clave: f.motivo, celdas: [f.etiqueta, f.cantidad, f.porcentaje] }))}
                                vacio="No se registraron motivos de no avance en el período."
                            />
                        </TarjetaReporte>
                    </Box>

                    {/* 4. Turnos atendidos por día */}
                    <TablaPorDia tabla={dias} />
                </Box>
            )}
        </Box>
    );
}

function TablaPorDia({ tabla }) {
    const columnas = [
        { titulo: 'Día' },
        { titulo: 'Atendidos' },
        ...tabla.resultados.map((r) => ({ titulo: r.etiqueta })),
    ];
    const filas = tabla.filas.map((f) => ({
        clave: f.fecha,
        atenuada: f.finDeSemana || f.atendidos === 0,
        celdas: [f.etiqueta, f.atendidos, ...f.valores],
    }));
    if (filas.length > 1) {
        filas.push({ clave: 'total', destacada: true, celdas: ['Total', tabla.totales.atendidos, ...tabla.totales.valores] });
    }
    return (
        <TarjetaReporte
            titulo="Turnos atendidos por día"
            subtitulo="Turnos en que la persona se presentó, día por día, y en qué terminó cada atención. Los fines de semana y los días sin atenciones aparecen atenuados."
        >
            <TablaReporte columnas={columnas} filas={filas} vacio="No hay días en el período seleccionado." />
        </TarjetaReporte>
    );
}

export default ReportesRecepcion;
